import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { img, srcSet } from "../lib/img";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { TrustBar } from "../components/TrustBar";
import { Reviews } from "../components/Reviews";
import { WhatsAppSection } from "../components/WhatsAppSection";
import { api } from "../lib/api";
import { waLink } from "../lib/whatsapp";
import { REGIONS, useSeo, localBusinessLd } from "../lib/seo";
import { serviceIcon } from "../lib/icons";

const HERO =
  "https://images.unsplash.com/photo-1561733841-5dfeab0b7b3a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const SPANS = [
  "md:col-span-8", "md:col-span-4", "md:col-span-4", "md:col-span-8",
  "md:col-span-6", "md:col-span-6", "md:col-span-5", "md:col-span-7",
];

export default function Home() {
  const [services, setServices] = useState([]);

  useSeo({
    configKey: "home",
    title: "Hausmeisterservice, Reinigung, Entrümpelung & Umzüge in Regensburg",
    description:
      "NENOX bietet Hausmeisterservice, Gebäudereinigung, Entrümpelung, Haushaltsauflösung, Entsorgung, Rückbau, Garten- und Außenarbeiten sowie Umzüge in Regensburg, Neumarkt, Nürnberg und Umgebung. Anfrage online konfigurieren, Fotos hochladen, Angebot erhalten.",
    canonical: "/",
    jsonLd: localBusinessLd(),
  });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]));
  }, []);

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden nx-noise nx-hero border-b border-[#E2E8F0]">
        <img
          src={img(HERO, 1280)}
          srcSet={srcSet(HERO, [640, 960, 1280, 1920])}
          sizes="100vw"
          alt="Helle moderne Wohnhausfassade als Beispiel für NENOX Dienstleistungen"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/92 to-white/55" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-44">
          <p className="nx-rise text-xs uppercase tracking-[0.35em] text-[#B24E00] font-semibold">
            Entrümpelung · Reinigung · Hausmeisterservice
          </p>
          <h1
            className="nx-rise nx-head mt-8 text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight max-w-4xl leading-[1.06] md:leading-[1.02]"
          >
            <span className="nx-silver">NENOX</span> – Professionelle Dienstleistungen
            <br className="hidden md:block" /> für Haus, Wohnung &amp; Gewerbe
          </h1>
          <p className="nx-rise mt-8 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
            Professionelle Entrümpelung, Reinigung, Entsorgung und Hausmeisterservice – zuverlässig, flexibel
            und kurzfristig für Privatkunden, Unternehmen, Immobilien und Gewerbe.
          </p>
          <p className="nx-rise mt-4 text-sm md:text-base text-zinc-500 max-w-2xl leading-relaxed">
            Wir sind in Regensburg, Nürnberg, Neumarkt, Ingolstadt, Passau und zahlreichen weiteren Städten
            und Regionen für Sie im Einsatz.
          </p>
          <p className="nx-rise mt-6 text-sm font-semibold text-[#145DA0]">
            Schnelle Termine · Faire Preise · Zuverlässige Ausführung
          </p>
          <div className="nx-rise mt-10 flex flex-col sm:flex-row gap-4" style={{ animationDelay: "0.15s" }}>
            <a href="#leistungen" data-testid="hero-select-service" className="nx-btn-action">Angebot anfragen</a>
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp"
              className="nx-btn-ghost"
            >
              <MessageCircle size={17} strokeWidth={1.9} /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      <TrustBar />

      <section id="leistungen" className="bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#B24E00] font-semibold">Leistungen</div>
            <h2 className="nx-head mt-4 text-3xl md:text-4xl tracking-tight font-medium">
              Wählen Sie Ihre Dienstleistung
            </h2>
          </div>
          <p className="text-sm text-zinc-600 max-w-sm">
            Jede Kategorie führt Sie direkt in den Anfrage-Konfigurator – mit allen relevanten Angaben und Foto-Upload.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6" data-testid="service-grid">
          {services.map((s, i) => {
            const Icon = serviceIcon(s.icon);
            return (
              <div key={s.slug} className={SPANS[i % SPANS.length]}>
                <Link
                  to={`/leistungen/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group relative block h-full min-h-[220px] bg-white border border-[#E2E8F0] rounded-xl overflow-hidden hover:border-[#145DA0]/60 hover:shadow-lg"
                  style={{ transition: "border-color .25s ease" }}
                >
                  <img
                    src={img(s.image, 720)}
                    srcSet={srcSet(s.image, [400, 720, 1000])}
                    sizes="(max-width: 768px) 100vw, 50vw"
                    alt={s.image_alt || s.name}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105"
                    style={{ transition: "transform .6s ease" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/92 to-white/45" />
                  <div className="relative p-7 md:p-9 h-full flex flex-col justify-between">
                    <Icon size={30} strokeWidth={1.4} className="text-[#145DA0]" />
                    <div>
                      <h3 className="nx-head text-xl md:text-2xl tracking-tight font-semibold text-[#0F172A]">{s.name}</h3>
                      <p className="mt-2 text-sm text-zinc-600">{s.tagline}</p>
                      <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#145DA0] font-semibold">
                        Auswählen <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
      </section>

      <section className="bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#B24E00] font-semibold">Ablauf</div>
            <h2 className="nx-head mt-4 text-3xl md:text-4xl tracking-tight font-medium">
              In wenigen Schritten zum Angebot
            </h2>
            <p className="mt-5 text-sm text-zinc-600 max-w-md leading-relaxed">
              Sie geben online alle wichtigen Informationen ein. NENOX prüft Ihre Angaben und Fotos und
              erstellt ein individuelles Angebot – bei Bedarf nach kostenloser Besichtigung.
            </p>
            <Link
              to="/besichtigung"
              className="nx-btn mt-10"
            >
              Kostenlose Besichtigung
            </Link>
          </div>
          <ol className="space-y-5">
            {[
              "Dienstleistung auswählen",
              "Details, Fläche und Menge eingeben",
              "Zusatzleistungen auswählen",
              "Fotos hochladen",
              "Adresse und Wunschtermin angeben",
              "Anfrage senden – individuelles Angebot erhalten",
            ].map((t, i) => (
              <li key={t} className="flex gap-5 items-start border-b border-[#E2E8F0] pb-4">
                <span className="nx-head text-sm text-[#145DA0] font-semibold w-8">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm text-zinc-800">{t}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <Reviews />

      <WhatsAppSection />

      <section className="bg-[#F8FAFC] border-t border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="text-xs uppercase tracking-[0.3em] text-[#B24E00] font-semibold">Einsatzgebiete</div>
          <h2 className="nx-head mt-4 text-3xl md:text-4xl tracking-tight font-medium">
            Regensburg und weit darüber hinaus
          </h2>
          <p className="mt-5 text-sm text-zinc-600 max-w-2xl leading-relaxed">
            Wir sind in der Oberpfalz, Niederbayern, Mittelfranken und im Raum Ingolstadt für Sie unterwegs.
            Wählen Sie Ihre Region und sehen Sie die passenden Leistungen an Ihrem Standort.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            {REGIONS.map((r) => (
              <Link
                key={r.slug}
                to={`/einsatzgebiete/${(r.places.find((p) => p.main) || r.places[0]).slug}`}
                data-testid={`home-region-${r.slug}`}
                className="border border-[#145DA0]/30 rounded-lg px-5 py-3 text-sm uppercase tracking-[0.12em] text-[#145DA0] hover:bg-[#EBF5FF]"
                style={{ transition: "background-color .2s ease" }}
              >
                {r.name}
              </Link>
            ))}
          </div>
          <Link
            to="/einsatzgebiete"
            data-testid="home-einsatzgebiete-cta"
            className="nx-btn mt-10"
          >
            Alle Einsatzgebiete
          </Link>
        </div>
      </section>
    </div>
  );
}
